import useModal from '@area2k/use-modal';
import { Share } from '@capacitor/share';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { faCheckCircle } from '@fortawesome/free-regular-svg-icons';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { CloseOutlined, MoreVert, OpenInNew } from '@mui/icons-material';
import {
  IconButton,
  Menu,
  MenuItem,
  MenuList,
  Snackbar,
  IconButton as MuiIconButton,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, Navigate, useNavigate } from 'react-router-dom';

import PageSkeleton from './PageSkeleton';
import WorkerCompleted from './WorkerCompleted';
import WorkerInProgress from './WorkerInProgress';
import LiftSuspensionModal from '../WorkerV2/components/LiftSuspensionModal';
import UpdatePasswordToWorkerModal, {
  WorkerItem,
} from './modals/UpdatePasswordToWorkerModal';
import useChangeAvatarModal from './useChangeAvatarModal';

import { CameraIcon, GravyTrained, LinkIcon, ResetIcon } from '@/assets/icons';
import Alert from '@/components/Alert';
import Avatar from '@/components/Avatar';
import Badge from '@/components/Badge';
import Page from '@/components/Page';
import RatingField from '@/components/RatingField';
import Stack from '@/components/Stack';
import { Body, Small, Title } from '@/components/Typography';
import WorkerSuspensionBanner from '@/components/WorkerSuspensionBanner';
import Config from '@/config';
import { FEATURE_TOGGLE } from '@/constants/featuretoggle';
import { TIME_TO_REFRESH } from '@/constants/general';
import { useGetWorkerQuery } from '@/graphql';
import useAuth from '@/hooks/useAuth';
import ROUTES from '@/routes/routes';
import { accountabilityAPI } from '@/services/accountabilityAPI';
import {
  CandidateStatusEnum,
  SsnVerificationStatusEnum,
  WorkerStatusEnum,
} from '@/types/graphql';
import { WorkerDetail, WorkersV2API } from '@/services/workersV2API';
import { isWeb } from '@/util/platform';
import { containsGravyCertification } from '@/util/worker';

import { WarningAmberOutlined } from '@mui/icons-material';

import './index.css';

const Worker = () => {
  const { workerId } = useParams();
  const navigate = useNavigate();
  const filterDate = useMemo(() => new Date().toISOString(), []);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [toastMsg, setToastMsg] = useState('');
  const [workerDetail, setWorkerDetail] = useState<WorkerDetail | null>(null);
  const [showLiftSuspensionModal, setShowLiftSuspensionModal] = useState(false);
  const [liftingSuspension, setLiftingSuspension] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [canUnsuspend, setCanUnsuspend] = useState(false);

  const handleClose = (event: any, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setToastMsg('');
  };

  const fetchRestWorkerData = useCallback(() => {
    if (!workerId) return;

    WorkersV2API.getWorker(parseInt(workerId))
      .then((res) => {
        if (res.success) {
          setWorkerDetail(res.data);
        }
      })
      .catch(() => {});
  }, [workerId]);

  useEffect(() => {
    fetchRestWorkerData();

    accountabilityAPI
      .getClearPermission()
      .then((res) => {
        setCanUnsuspend(!!(res.success && res.can_clear_violation));
      })
      .catch(() => {});
  }, [fetchRestWorkerData]);

  const query = useGetWorkerQuery({
    variables: { filterDate, workerId },
    pollInterval: TIME_TO_REFRESH,
  });
  const ratingEnabled = useFeatureValue(FEATURE_TOGGLE.WorkerRating, false);
  const profileRevamp = useFeatureValue(
    FEATURE_TOGGLE.WorkerProfileRevamp,
    false,
  );
  const employmentEligibilityCompliancely = useFeatureValue(
    FEATURE_TOGGLE.EmploymentEligibilityCompliancely,
    false,
  );
  const { currentAdminIsCustomerAdmin } = useAuth();
  const showAvatarModal = useChangeAvatarModal(query.data?.worker);
  const [showPasswordModal, hidePasswordModal] = useModal(
    (worker: WorkerItem) => (
      <UpdatePasswordToWorkerModal
        hideModal={hidePasswordModal}
        worker={worker}
      />
    ),
    [query.data],
  );

  const copyLink = async (workerId) => {
    handleCloseMenu();
    if (isWeb()) {
      try {
        navigator.clipboard.writeText(
          window.location.hostname + '/workers/' + workerId,
        );
        setToastMsg('Link Copied');
      } catch {
        setToastMsg('Unable to copy link');
      }
    } else {
      try {
        await Share.share({
          url: Config.BASE_URL + '/workers/' + workerId,
        });
      } catch {
        setToastMsg('Unable to share link');
      }
    }
  };

  const handleOpenLiftSuspensionModal = () => {
    setActionError(null);
    setActionSuccess(null);
    setShowLiftSuspensionModal(true);
  };

  const getErrorMessage = (err: unknown, fallback: string) => {
    const responseData = (
      err as {
        response?: {
          data?: {
            message?: string;
            errors?: string[];
          };
        };
      }
    )?.response?.data;

    if (responseData?.message) return responseData.message as string;
    if (
      Array.isArray(responseData?.errors) &&
      (responseData.errors as string[]).length > 0
    ) {
      return (responseData.errors as string[]).join(', ');
    }

    if (err instanceof Error && err.message) return err.message;
    return fallback;
  };

  const handleLiftSuspension = async (reason: string) => {
    if (!workerDetail?.active_suspension?.id) {
      throw new Error('No active suspension found for this worker');
    }

    setLiftingSuspension(true);
    setActionError(null);
    setActionSuccess(null);

    try {
      const response = await WorkersV2API.liftSuspension(
        workerDetail.active_suspension.id,
        reason,
      );

      if (!response.success) {
        throw new Error(
          response.message ||
            response.errors?.join(', ') ||
            'Failed to lift suspension',
        );
      }

      await query.refetch();
      fetchRestWorkerData();
      setActionSuccess('Worker suspension lifted successfully');
    } catch (err: unknown) {
      throw new Error(getErrorMessage(err, 'Failed to lift suspension'));
    } finally {
      setLiftingSuspension(false);
    }
  };

  if (!query.data) {
    return <PageSkeleton />;
  }

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const worker = query.data.worker;
  const isWorkerInProgress =
    worker.candidateStatus === CandidateStatusEnum.START_HIRE &&
    worker.status !== WorkerStatusEnum.HIRED;

  if (currentAdminIsCustomerAdmin && isWorkerInProgress) {
    return <Navigate to={ROUTES.basePath} />;
  }
  const isCertified = containsGravyCertification(worker.activeCertificates);
  return (
    <Page
      headerMenu={
        !currentAdminIsCustomerAdmin && (
          <Stack style={{ justifyContent: 'flex-end', flex: 1 }}>
            <IconButton
              aria-label="menu-icon"
              id="menu-icon"
              onClick={(e) => setAnchorEl(e.currentTarget)}
            >
              <MoreVert />
            </IconButton>
            <Menu
              MenuListProps={{
                'aria-labelledby': 'basic-button',
              }}
              PaperProps={{
                sx: {
                  border: '1px solid #D3D3D3',
                  filter:
                    'drop-shadow(0px 4.050473213195801px 4.050473213195801px rgba(0, 0, 0, 0.05))',
                  boxShadow: 'none',
                },
              }}
              anchorEl={anchorEl}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              id="basic-menu"
              open={open}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              onClose={handleCloseMenu}
            >
              <MenuList
                dense={true}
                sx={{ paddingTop: '0px', paddingBottom: '0px' }}
              >
                <MenuItem id="change-picture" onClick={showAvatarModal}>
                  <CameraIcon fontSize="small" />
                  <span style={{ marginLeft: '12px' }}>Change Picture</span>
                </MenuItem>
                <MenuItem
                  id="reset-password"
                  onClick={() => showPasswordModal(worker)}
                >
                  <ResetIcon fontSize="small" />
                  <span style={{ marginLeft: '10px' }}>Reset Password</span>
                </MenuItem>
                <MenuItem id="share-link" onClick={() => copyLink(worker.id)}>
                  <LinkIcon fontSize="small" />
                  <span style={{ marginLeft: '10px' }}>Share Link</span>
                </MenuItem>
                <MenuItem
                  id="view-v2"
                  onClick={() => {
                    handleCloseMenu();
                    navigate(`/workers_v2/${worker.id}`);
                  }}
                >
                  <OpenInNew fontSize="small" />
                  <span style={{ marginLeft: '10px' }}>View in V2</span>
                </MenuItem>
              </MenuList>
            </Menu>
          </Stack>
        )
      }
      media={
        profileRevamp ? (
          <div style={{ position: 'relative' }}>
            <Avatar
              border={isCertified}
              firstName={worker.user.firstName}
              rounded={true}
              size="lg"
              src={worker.avatarUrl}
            />
            {isCertified && (
              <GravyTrained
                sx={{
                  position: 'absolute',
                  right: 0,
                  bottom: 0,
                  width: '22px',
                  height: '22px',
                }}
              />
            )}
          </div>
        ) : (
          <Avatar
            firstName={worker.user.firstName}
            size="md"
            src={worker.avatarUrl}
          />
        )
      }
      title={
        <Stack vertical style={{ flex: 5 }}>
          <Stack>
            <Title className="complete-worker-name">
              {worker.user.firstName} {worker.user.middleName}{' '}
              {worker.user.lastName}
            </Title>
            <Badge
              label={worker.active ? 'Enabled' : 'Disabled'}
              status={worker.active ? 'theme' : 'danger'}
            />
            {employmentEligibilityCompliancely &&
              worker.verificationStatus !==
                SsnVerificationStatusEnum.VERIFIED && (
                <span className="eligibility-unverified">
                  <WarningAmberOutlined className="eligibility-unverified-icon" />
                  Eligibility Unverified
                </span>
              )}
          </Stack>
          {ratingEnabled &&
            worker.candidateStatus === CandidateStatusEnum.COMPLETED && (
              <Stack vertical gap={4}>
                {worker.jobsRatingCount > 0 && (
                  <RatingField
                    initialRating={worker.averageRating}
                    readOnly={true}
                    showNumberLabel={true}
                  />
                )}
                <Stack
                  gap={4}
                  style={{
                    borderTop: '1px solid #DFE1E6',
                    paddingTop: '10px',
                    width: 'auto',
                  }}
                >
                  <Small>Shifts Worked:</Small>
                  <Body weight={'semibold'}>{worker.jobsWorked}</Body>
                  <div style={{ width: '5px' }} />
                  <Small>Shifts Dropped:</Small>
                  <Body weight={'semibold'}>
                    {workerDetail?.stats?.jobs_dropped ?? worker.jobsDropped}
                  </Body>
                  <div style={{ width: '5px' }} />
                  <Small>No Show:</Small>
                  <Body weight={'semibold'}>
                    {workerDetail?.stats?.no_show ?? worker.noShow ?? 0}
                  </Body>
                </Stack>
              </Stack>
            )}
        </Stack>
      }
    >
      {showLiftSuspensionModal && workerDetail && (
        <LiftSuspensionModal
          workerName={workerDetail.user?.full_name || ''}
          onClose={() => setShowLiftSuspensionModal(false)}
          onSubmit={handleLiftSuspension}
        />
      )}
      {workerDetail &&
        (workerDetail.is_suspended ||
          !workerDetail.active ||
          actionError ||
          actionSuccess) && (
          <Stack vertical gap={16} style={{ marginBottom: '16px' }}>
            <WorkerSuspensionBanner
              worker={workerDetail}
              onLiftSuspension={
                canUnsuspend ? handleOpenLiftSuspensionModal : undefined
              }
              liftingSuspension={liftingSuspension}
            />
            {(actionError || actionSuccess) && (
              <Stack vertical gap={8}>
                {actionError && (
                  <Alert
                    description={actionError}
                    icon={faExclamationTriangle}
                    status="danger"
                    title="Error"
                  />
                )}
                {actionSuccess && (
                  <Alert
                    description={actionSuccess}
                    icon={faCheckCircle}
                    status="success"
                    title="Success"
                  />
                )}
              </Stack>
            )}
          </Stack>
        )}
      {isWorkerInProgress ? (
        <WorkerInProgress
          currentAdminIsCustomerAdmin={currentAdminIsCustomerAdmin}
          worker={worker}
        />
      ) : (
        <WorkerCompleted
          currentAdminIsCustomerAdmin={currentAdminIsCustomerAdmin}
          worker={worker}
        />
      )}
      {toastMsg && (
        <Snackbar
          action={
            <MuiIconButton
              color="inherit"
              id="close-notification"
              size="small"
              onClick={handleClose}
            >
              <CloseOutlined fontSize="small" />
            </MuiIconButton>
          }
          id="toast-msg"
          message={toastMsg}
          open={!!toastMsg}
          onClose={handleClose}
        />
      )}
    </Page>
  );
};

export default Worker;
